import React, { useCallback, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { LayoutPage, LayoutForm, Form, FormData } from 'components';
import { useAppContext } from 'hooks/useAppContext';
import { DocUpload, UploadMethods } from 'doc-upload';
import { submitHandler } from 'utils';
import { Routes } from 'types';
import { useTrackingEvent } from 'hooks';
import { setComplete } from 'context/actions';
import { Title } from 'components/page-title/PageTitle';
import { IconWrapper } from 'views/upload/styles';
import { Text, Link, Container, Stack, List, Accordion } from 'basis';
import { toDocUploadProps } from 'views/upload/toDocUploadProps';
import { MoneyDocumentsIcon } from 'components/icons';
import styled from '@emotion/styled';

const initialValues = {
  reference: '',
  documentType: '',
};

const StrongWindowsCompatible = styled.strong`
  font-weight: 600 !important;
`;

export const NzAmlDocumentsUpload: React.FC = () => {
  const history = useHistory();
  const { state, dispatch } = useAppContext();
  const methodsRef = useRef<UploadMethods | undefined>();
  const { trackEvent } = useTrackingEvent();
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = useCallback(
    ({ errors, values }) => {
      submitHandler({
        errors,
        submit: async () => {
          setSubmitting(true);
          const result = await methodsRef.current?.startUpload?.();
          setSubmitting(false);

          if (result?.ok) {
            trackEvent({
              category: 'application',
              action: 'application-submitted',
              location: window.location.pathname.slice(1),
              label: 'submit',
            });

            dispatch(setComplete(true));
            history.push(Routes.amlNzSubmitted);
          }
        },
      });
    },
    [dispatch, history, trackEvent],
  );

  return (
    <LayoutPage step="update">
      <Form
        id="nzAmlDocumentsUpload"
        initialValues={initialValues}
        onSubmit={onSubmit}
        loading={submitting}
        submitButtonLabel={submitting ? 'Submitting...' : 'Submit'}
      >
        {(formData: FormData) => {
          return (
            <LayoutForm>
              <Title>Upload your documents</Title>
              <IconWrapper>
                <MoneyDocumentsIcon width={49} height={61} />
                <Text>Documents will take 1-2 business days to review.</Text>
              </IconWrapper>
              <NzDocumentsAccordion />
              <DocUpload {...toDocUploadProps({ state, formData, methodsRef })} />
            </LayoutForm>
          );
        }}
      </Form>
    </LayoutPage>
  );
};

function NzDocumentsAccordion() {
  return (
    <Container margin="0 0 24px 0">
      <Stack gap="2">
        <Text color="grey.t75" textStyle="heading6">
          What documents do I need?
        </Text>
        <Accordion color="grey.t07" itemGap="large" itemHeaderAs="h3">
          <Accordion.Item>
            <Accordion.Item.Header>Proof of Address</Accordion.Item.Header>
            <Accordion.Item.Content>
              <Text margin="0 0 16px 0">
                To verify your address, we require one of the following documents that
                <StrongWindowsCompatible>
                  {' '}
                  includes your name and address, from the last 6 months
                </StrongWindowsCompatible>
                .
              </Text>
              <br />
              <List>
                <List.Item>Utility bill</List.Item>
                <List.Item>Rates notice</List.Item>
                <List.Item>Bank statement or credit card statement</List.Item>
                <List.Item>Loan statement</List.Item>
                <List.Item>Home and contents insurance policy (12 months for annual policy)</List.Item>
                <List.Item>Government issued document</List.Item>
                <List.Item>Rental tenancy agreement</List.Item>
                <List.Item>Property purchase and sale agreements</List.Item>
                <List.Item>Letter from educational institutions</List.Item>
                <List.Item>KiwiSaver statement</List.Item>
              </List>
            </Accordion.Item.Content>
          </Accordion.Item>

          <Accordion.Item>
            <Accordion.Item.Header>Identity Verification</Accordion.Item.Header>
            <Accordion.Item.Content>
              <Text margin="0 0 16px 0">
                Provide us a <StrongWindowsCompatible>certified copy</StrongWindowsCompatible> of your New Zealand
                Driver Licence, Birth Certificate, NZ Passport or AU Passport (not expired more than 2 years).
              </Text>
              <br />
              <Text margin="0 0 16px 0">
                You must take your original document and a copy to a Trusted Referee in New Zealand. Our preferred list
                of Trusted Referees includes:
              </Text>
              <br />
              <List>
                <List.Item>Justice of the Peace</List.Item>
                <List.Item>Registered Medical Doctor</List.Item>
                <List.Item>Registered Teacher</List.Item>
                <List.Item>
                  Lawyer (as defined in the{' '}
                  <Link href="http://www.legislation.govt.nz/act/public/2006/0001/latest/whole.html#dlm364939" newTab>
                    Lawyers and Conveyancers Act 2006
                  </Link>
                  )
                </List.Item>
                <List.Item>Home and contents insurance policy (12 months for annual policy)</List.Item>
                <List.Item>Member of Parliament, or</List.Item>
                <List.Item>
                  Chartered Accountant (within the meaning of{' '}
                  <Link
                    href="http://www.legislation.govt.nz/act/public/1993/0105/latest/link.aspx?id=DLM391422#DLM391422"
                    newTab
                  >
                    section 19
                  </Link>{' '}
                  of the New Zealand Institute of Chartered Accountants Act 1996)
                </List.Item>
                <List.Item>Property purchase and sale agreements</List.Item>
              </List>
            </Accordion.Item.Content>
          </Accordion.Item>
        </Accordion>
      </Stack>
    </Container>
  );
}
